import React, { useEffect, useState } from "react";
import moment from "moment";

import V2PageHeader from "../../../v2/components/V2PageHeader";
import V2Card from "../../../v2/components/V2Card";
import ChecklistMatrixTable from "../shared/ChecklistMatrixTable";
import ChecklistStatusLegend from "../shared/ChecklistStatusLegend";
import useChecklistStatusFilter from "../shared/useChecklistStatusFilter";
import { getMe, getStoreTeamProgress, getRoleDetail } from "../../../helpers/checklistApi";
import { getUserStoreId } from "../../../helpers/permissions";
import { adaptStoreManagerMatrix } from "../shared/adaptStoreManagerData";

import "./index.css";

const TODAY_LABEL = moment().format("dddd, D MMMM YYYY");
const EMPTY_MATRIX = { stores: [], items: [], sectionSummary: [], summary: null };

// Read-only: the store-manager endpoints only cover the manager's own store,
// so the matrix has a single store column.
export default function StoreManagerView() {
  const [loading, setLoading] = useState(true);
  const [storeId, setStoreId] = useState(getUserStoreId());
  const [profile, setProfile] = useState(null);
  const [matrix, setMatrix] = useState(EMPTY_MATRIX);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const me = await getMe();
      const resolvedStoreId = getUserStoreId() ?? me?.storeId ?? null;
      if (cancelled) return;
      setProfile(me);
      setStoreId(resolvedStoreId);
      if (!resolvedStoreId) {
        setLoading(false);
        return;
      }

      const teamProgress = (await getStoreTeamProgress(resolvedStoreId)) || [];
      const roleDetails = await Promise.all(
        teamProgress.map((role) => getRoleDetail(resolvedStoreId, role.roleId))
      );
      if (cancelled) return;
      setMatrix(
        adaptStoreManagerMatrix({
          storeId: resolvedStoreId,
          storeName: me?.store?.name || me?.storeName || `Store ${resolvedStoreId}`,
          managerName: me?.name,
          teamProgress,
          roleDetails,
        })
      );
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const { stores, items, sectionSummary, summary } = matrix;
  const storeIds = stores.map((store) => store.id);
  const { activeStatuses, toggleStatus, visibleItemCount, totalItemCount } = useChecklistStatusFilter(
    items,
    storeIds
  );

  const knownStoreName = profile?.store?.name || profile?.storeName;
  const subtitle = [knownStoreName, profile?.name, TODAY_LABEL].filter(Boolean).join(" · ");

  const summaryRow = (
    <div className="store-manager-summary-row">
      <V2Card value={summary?.done} label="Items done" tone="green" loading={loading} />
      <V2Card value={summary?.flagged} label="Flagged" tone="red" loading={loading} />
      <V2Card value={summary?.pending} label="Pending" tone="default" loading={loading} />
      <V2Card value={`${summary?.overallPercent}%`} label="Overall progress" tone="default" loading={loading} />
    </div>
  );

  let body;
  if (loading) {
    body = (
      <>
        {summaryRow}
        <ChecklistMatrixTable loading skeletonColumnCount={1} stores={[]} items={[]} sectionSummary={[]} />
      </>
    );
  } else if (!storeId) {
    body = (
      <div className="store-manager-empty-state">
        <p className="store-manager-empty-title">No store assigned</p>
        <p className="store-manager-empty-hint">
          This login isn't linked to a store. Log out and log back in with a store manager account.
        </p>
      </div>
    );
  } else if (items.length === 0) {
    body = (
      <div className="store-manager-empty-state">
        <p className="store-manager-empty-title">No checklist items for today</p>
      </div>
    );
  } else {
    body = (
      <>
        {summaryRow}

        <ChecklistStatusLegend
          activeStatuses={activeStatuses}
          onToggleStatus={toggleStatus}
          visibleItemCount={visibleItemCount}
          totalItemCount={totalItemCount}
        />

        <ChecklistMatrixTable
          stores={stores}
          items={items}
          sectionSummary={sectionSummary}
          activeStatuses={activeStatuses}
          showPhotos
        />
      </>
    );
  }

  return (
    <div className="v2-scope store-manager-view">
      <V2PageHeader
        title="Daily checklist (Store Manager)"
        subtitle={subtitle}
        action={<span className="v2-role-pill">STORE MANAGER</span>}
      />
      {body}
    </div>
  );
}
